import type { SessionSummary } from '@perplexicon/api-client';

import { resolveGameProfileArt } from '../../../assets/asset-manifest.js';
import { Card, GameActionLink, StatusChip } from '../../../components/index.js';
import { GAME_PROFILE_ART } from '../../../games/registry.js';
import {
  opponentNames,
  relativeTimeLabel,
  roundLabel,
  turnLabel,
} from '../../../lib/session-display.js';

export interface ResumeGameCardProps {
  entry: SessionSummary;
  /** The viewer's own user id, used to render "opponent"/"whose turn" from their perspective. */
  selfId: string;
}

/**
 * A compact "resume strip" for one in-progress session (approved Games-page
 * mockup "CONTINUE PLAYING") — deliberately flatter and quieter than
 * `GameCatalogCard`: a small emblem, name, two lines of real session
 * metadata, and one compact `Continue` control. No scenic banner, no mode
 * icons, no description — those belong to the catalog card below it.
 *
 * Every field comes from the platform's own `SessionSummary` (game name,
 * opponent, current-state summary, whose turn, current round, last activity)
 * — no game-specific rendering exists here (CLAUDE.md §2). The emblem reuses
 * the game's own profile-card icon (`GAME_PROFILE_ART`) rather than a new
 * art slot, so a game needs no extra artwork to appear here.
 */
export function ResumeGameCard({ entry, selfId }: ResumeGameCardProps) {
  const turn = turnLabel(entry, selfId);
  const round = roundLabel(entry);
  const yourTurn = entry.currentTurnUserId === selfId;
  const icon = resolveGameProfileArt(undefined, GAME_PROFILE_ART[entry.gameId]).icon;

  return (
    <Card
      variant="parchment"
      className="flex flex-col gap-2 p-3 shadow-1 tablet:flex-row tablet:items-center tablet:gap-3.5"
    >
      <img
        src={icon}
        alt=""
        aria-hidden="true"
        width={216}
        height={216}
        className="h-12 w-12 shrink-0 self-start rounded-md object-contain tablet:h-14 tablet:w-14 tablet:self-center"
        draggable={false}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {/* The game's own name is short and important enough to keep in
              full rather than ellipsizing it at the narrower single-column
              widths this strip can land at (docs/UI_GUIDELINES.md §9). */}
          <p className="font-display text-base font-semibold text-textPrimary">{entry.gameName}</p>
          {yourTurn && <StatusChip tone="active">Your turn</StatusChip>}
        </div>
        <p className="truncate text-xs text-textSecondary">
          vs {opponentNames(entry, selfId)}
          {round && <span className="text-textMuted"> · {round}</span>}
        </p>
        <p className="truncate text-xs text-textMuted">
          {yourTurn ? entry.summary.headline : (turn ?? entry.summary.headline)}
          {' · '}
          {relativeTimeLabel(entry.lastActionAt)}
        </p>
      </div>
      <GameActionLink
        to={`/play/${entry.id}`}
        size="sm"
        className="shrink-0 self-end tablet:self-center"
      >
        Continue
        <span className="sr-only"> — {entry.gameName}</span>
      </GameActionLink>
    </Card>
  );
}
